/**
 * What was billed, what arrived, and what is still owed.
 *
 * Two lines rather than one, the same pair the dashboard's revenue card draws:
 * the gap between invoiced and collected is the only thing this chart can say
 * that a single total cannot.
 */
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { fmtMoney, fmtNum } from "../../ui";
import { Breakdown, MonthChart, Stat, StatStrip } from "./bits";
import type { SalesReport } from "../../../lib/reports";

export function SalesReportTab({ data }: { data: SalesReport }) {
  const t = useT();
  const { locale } = useI18n();
  const { totals } = data;

  return (
    <>
      <StatStrip columns={5}>
        <Stat label={t("reports.sales.invoiced")} value={fmtMoney(totals.invoicedCents)} sub={fmtNum(totals.invoiceCount) + " " + t("reports.sales.docsUnit")} />
        <Stat label={t("reports.sales.collected")} value={fmtMoney(totals.collectedCents)} tone="ok" />
        <Stat label={t("reports.sales.outstanding")} value={fmtMoney(totals.outstandingCents)} />
        <Stat label={t("reports.sales.overdue")} value={fmtMoney(totals.overdueCents)} tone={totals.overdueCents ? "bad" : undefined} />
        <Stat
          label={t("reports.sales.avg")}
          value={fmtMoney(totals.avgInvoiceCents)}
          sub={totals.creditCount ? t("reports.sales.credited", { v: fmtMoney(totals.creditedCents) }) : undefined}
        />
      </StatStrip>

      <div className="crm-rep-grid">
        <MonthChart
          title={t("reports.sales.chartTitle")}
          sub={t("reports.sales.chartSub")}
          months={data.meta.months}
          locale={locale}
          lines={[
            { label: t("reports.sales.invoiced"), values: data.months.map((m) => m.invoicedCents / 100), color: "var(--color-primary)" },
            { label: t("reports.sales.collected"), values: data.months.map((m) => m.collectedCents / 100), color: "var(--color-muted-foreground)" },
          ]}
        />
      </div>

      <div className="crm-rep-grid is-split">
        <Breakdown
          title={t("reports.sales.byStatus")}
          rows={data.byStatus.map((r) => ({
            key: r.status,
            label: t(`sales.status.${r.status}`),
            weight: r.totalCents,
            value: fmtMoney(r.totalCents),
            sub: fmtNum(r.count),
          }))}
          empty={t("reports.empty")}
        />
        <Breakdown
          title={t("reports.sales.topClients")}
          sub={t("reports.sales.topClientsSub")}
          rows={data.topClients.map((r) => ({
            key: r.clientId,
            label: r.name,
            weight: r.collectedCents,
            value: fmtMoney(r.collectedCents),
          }))}
          empty={t("reports.empty")}
          color="var(--color-success)"
        />
      </div>

      <p className="crm-rep-note">{t("reports.sales.note")}</p>
    </>
  );
}
